import React from "react";
import { TILES } from "../../../../../core/theme/reportTheme";
import { soFar, coverageNote, multiChannelShare, CHANNEL_NAMES, CHANNEL_TOOLTIPS, VISIBILITY_CHANNELS } from "../../../../../core/data/marketOverviewText";
import { channelGroups } from "../../../../../core/data/channelOrder";
import { useChannelStrips, ChannelStrip } from "../../../components/ChannelStrip";

// What each channel's big number counts.
const CHANNEL_UNITS = { ppc: "advertisers", local: "listings", seo: "websites", shopping: "sellers", lsa: "businesses" };
// Not part of Google's page layout: their own "Other platforms" group.
const OTHER_CHANNELS = ["ai", "bing"];

// One row, straight on the page (like Advertiser Overview's channel tiles):
//   Your searches: searches, advertisers seen, multi-channel (white, purple pills)
//   Google page layout: one tile per part of the page, filled with the
//     channel colour - seen first, numbered top to bottom, then none seen
//     (white, 0, like an empty Google Shopping tile). The order is shared
//     with the page tabs and Advertiser Overview's tiles (channelOrder.js).
//   Other platforms: AI Overview and Bing - white tiles saying NOT SEARCHED.
// The row has the same three columns as the panel row below it; in the two
// right-hand columns every tile is the same width.
// Channels with nothing found (none seen, not searched) are narrow strips
// that open into full tiles on hover (ChannelStrip.jsx); the groups stay
// together and share the width by how many full tiles they hold.
export default function TileRow({ result }) {
  const groups = channelGroups(result.serpMap);
  const block = channel => result.serpMap.find(b => b.channel === channel);
  const google = list => list.filter(c => !OTHER_CHANNELS.includes(c));
  const seen = google(groups.seen);
  const noneSeen = google(groups.noneSeen);
  const notSearched = google(groups.notChecked);
  const others = groups.notChecked.filter(c => OTHER_CHANNELS.includes(c));
  const strips = useChannelStrips();

  // [channel, isStrip] in the order shown.
  const googleTiles = [...seen.map(c => [c, false]), ...noneSeen.map(c => [c, true]), ...notSearched.map(c => [c, true])];
  const otherTiles = others.map(c => [c, true]);
  const drawGoogle = ([c, isStrip]) => {
    if (!strips.open(c, isStrip)) return <ChannelStrip channel={c} name={CHANNEL_NAMES[c]} title={`${CHANNEL_NAMES[c]}: ${notSearched.includes(c) ? "not searched yet" : "none seen"}`} />;
    if (notSearched.includes(c)) return <NotSearchedTile channel={c} />;
    const order = seen.indexOf(c);
    return <ChannelTile channel={c} block={block(c)} order={order >= 0 ? order + 1 : undefined} result={result} />;
  };
  const drawOther = ([c, isStrip]) => (strips.open(c, isStrip)
    ? <NotSearchedTile channel={c} />
    : <ChannelStrip channel={c} name={CHANNEL_NAMES[c]} title={`${CHANNEL_NAMES[c]}: not searched yet`} />);

  // Lined up with the panel row below: Your searches over Competition type,
  // the channel tiles over Gaps by postcode + Gaps by keyword.
  return (
    <div className={TILES.row}>
      <Group title={`Your searches${soFar(result)}`} units={3}>
        <SummaryTiles result={result} />
      </Group>
      <div className={TILES.channelSpan}>
        <Group title="Google page layout · top to bottom" units={strips.groupWeight(googleTiles)}>
          {googleTiles.map(t => <div key={t[0]} {...strips.slot(t[0], t[1])}>{drawGoogle(t)}</div>)}
        </Group>
        {others.length > 0 && (
          <Group title="Other platforms" units={strips.groupWeight(otherTiles)}>
            {otherTiles.map(t => <div key={t[0]} {...strips.slot(t[0], t[1])}>{drawOther(t)}</div>)}
          </Group>
        )}
      </div>
    </div>
  );
}

// A small title over its tiles; units = how many tiles, so tiles are equal widths.
function Group({ title, units, children }) {
  return (
    <div className={TILES.group} style={{ flexGrow: units, flexBasis: 0 }}>
      <p className={TILES.groupTitle} title={title}>{title}</p>
      <div className={TILES.tiles}>{children}</div>
    </div>
  );
}

// A tile: pill title (with the page order on the right), one big number
// (with an optional extra beside it), what it counts, then a small line.
function Tile({ fill, pill, name, order, value, extra, unit, foot, title }) {
  return (
    <div className={`${TILES.fill[fill]} ${TILES.full}`} title={title}>
      <div className={TILES.head}>
        <span className={TILES.pill[pill]}>{name}</span>
        {order && <span className={TILES.order}>{order}</span>}
      </div>
      <div className={TILES.valueRow}>
        {value == null
          ? <span className={TILES.notSearched}>Not searched</span>
          : <span className={TILES.big}>{value}</span>}
        {extra && <span className={TILES.extra}>{extra}</span>}
      </div>
      {unit && <p className={TILES.unit}>{unit}</p>}
      {foot && <p className={TILES.foot}>{foot}</p>}
    </div>
  );
}

function SummaryTiles({ result }) {
  const { totals, coverage } = result;
  const planned = `of ${coverage.searchesPlanned} planned`;
  const estimated = coverageNote(result);
  return (
    <>
      <Tile
        fill="summary" pill="summary" name="Searches"
        value={coverage.searchesCompleted} unit={planned}
        foot={estimated ? `(${estimated.label})` : null}
        title={`Searches: ${coverage.searchesCompleted} ${planned}${soFar(result)}${estimated ? `. ${estimated.title}` : ""}`}
      />
      <Tile
        fill="summary" pill="summary" name="Advertisers seen"
        value={totals.advertisers} unit="by website"
        title={`Advertisers seen: ${totals.advertisers} by website${soFar(result)}`}
      />
      <Tile
        fill="summary" pill="summary" name="Multi-channel"
        value={totals.multiChannel} extra={multiChannelShare(totals)} unit="appear in 2+ channels"
        title={`${totals.multiChannel} ${multiChannelShare(totals)} of ${totals.advertisers} advertisers seen in 2 or more of ${VISIBILITY_CHANNELS.replace(" or ", " and ")}`}
      />
    </>
  );
}

// A channel AdAudit doesn't search yet (AI Overview, Bing): white, like an
// empty Google Shopping tile, with NOT SEARCHED where the number goes.
function NotSearchedTile({ channel }) {
  const name = CHANNEL_NAMES[channel];
  return <Tile fill="noneSeen" pill={channel} name={name} value={null} foot="AdAudit doesn't search this yet" title={`${name}: not searched yet${CHANNEL_TOOLTIPS[channel] ? ` · ${CHANNEL_TOOLTIPS[channel]}` : ""}`} />;
}

// A Google channel: filled with its colour when someone was seen, white when not.
function ChannelTile({ channel, block, order, result }) {
  const name = CHANNEL_NAMES[channel];
  const value = result.totals[channel];
  const unnamed = channel === "shopping" ? result.totals.shoppingUnidentified : 0;
  const shown = block.shownIn > 0 ? `${block.shownIn} of ${block.of} searches` : `none seen · 0 of ${block.of}`;
  const foot = unnamed > 0 ? `${shown} · +${unnamed} unnamed ads` : shown;
  return (
    <Tile
      fill={block.shownIn > 0 ? channel : "noneSeen"} pill={channel} name={name} order={order}
      value={value} unit={CHANNEL_UNITS[channel]} foot={foot}
      title={`${order ? `${order} on the page · ` : ""}${name}: ${value} ${CHANNEL_UNITS[channel]} · ${foot}`}
    />
  );
}
